import { useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  isSortableField,
  SORTABLE_FIELDS,
  sortFieldLabel,
  type SortDirection,
  type SortSpec,
} from "./filterTypes";

interface SortEditorPopoverProps {
  sorts: ReadonlyArray<SortSpec>;
  onChange: (next: SortSpec[]) => void;
}

/** Local-only shape for the pending "+ Add sort" row. Stays in the
 *  editor's own state until the user picks a field; only then is it
 *  materialised into the parent's sorts array. Discarded on popover
 *  close or × on the row — no toast, no parent call. */
interface DraftSort {
  field: string | null;
  dir: SortDirection;
}

/** Multi-sort editor — list of sort rows with [up/down] [field ▾]
 *  [dir ▾] [×] + "+ Add sort" button. Materialised rows apply LIVE
 *  so the table refreshes as the operator edits.
 *
 *  The "+ Add sort" button now creates a LOCAL draft row that does
 *  NOT touch sorts until the user picks a field (F4-bis fix). This
 *  removes the intrusive default-sort-on-add that triggered an
 *  unwanted fetch immediately after the click.
 *
 *  Reorder uses up/down arrows rather than HTML5 drag-and-drop —
 *  the spec allows this as the simpler path (drag would budget more
 *  than ~30 lines once cross-row drop targets are handled). Up/down
 *  is keyboard-accessible by default. */
export function SortEditorPopover({
  sorts,
  onChange,
}: SortEditorPopoverProps) {
  // Field list used by the field dropdown. The materialised rows
  // disable already-used fields to prevent duplicates.
  const usedFields = new Set(sorts.map((s) => s.field));
  const firstUnused = SORTABLE_FIELDS.find((f) => !usedFields.has(f.key));

  // Draft row state — local-only, never lifted to the parent until
  // the user picks a field. Discarded on unmount (popover close).
  const [draft, setDraft] = useState<DraftSort | null>(null);

  function update(idx: number, patch: Partial<SortSpec>) {
    onChange(sorts.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }
  function remove(idx: number) {
    onChange(sorts.filter((_, i) => i !== idx));
  }
  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= sorts.length) return;
    const next = sorts.slice();
    const tmp = next[idx];
    next[idx] = next[target];
    next[target] = tmp;
    onChange(next);
  }
  function add() {
    if (!firstUnused) return; // All sortable fields already in the sort list.
    if (draft !== null) return; // One draft at a time (F4-bis D5).
    setDraft({ field: null, dir: "asc" });
  }
  /** Materialise the draft into sorts on first valid field pick.
   *  Clears the draft so the row now belongs to the parent's state. */
  function commitDraft(field: string) {
    if (!draft) return;
    onChange([...sorts, { field, dir: draft.dir }]);
    setDraft(null);
  }

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        Sort
      </div>
      {sorts.length === 0 && draft === null ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs italic text-slate-500">
          No sorts applied. The default order is used (highest score
          first).
        </p>
      ) : (
        <ul className="space-y-1.5">
          {sorts.map((sort, idx) => {
            const isReadOnly = !isSortableField(sort.field);
            return (
              <li
                key={`${sort.field}-${idx}`}
                className="flex items-center gap-1.5"
              >
                <span className="flex items-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {idx + 1}
                </span>
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Move sort up"
                    className={cn(
                      "h-3 w-5 rounded text-slate-400 hover:text-slate-700",
                      idx === 0 && "cursor-not-allowed opacity-30",
                    )}
                  >
                    <ArrowUp className="mx-auto h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === sorts.length - 1}
                    aria-label="Move sort down"
                    className={cn(
                      "h-3 w-5 rounded text-slate-400 hover:text-slate-700",
                      idx === sorts.length - 1 &&
                        "cursor-not-allowed opacity-30",
                    )}
                  >
                    <ArrowDown className="mx-auto h-3 w-3" />
                  </button>
                </div>
                {isReadOnly ? (
                  // Stale field key from an old saved view that
                  // references a field the backend no longer
                  // accepts. We surface the warning but DON'T edit:
                  // the operator can only remove it.
                  <span
                    className="flex h-8 flex-1 items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 text-xs text-amber-700"
                    title="This sort field is no longer supported. Remove it."
                  >
                    <AlertTriangle className="h-3 w-3" />
                    <span className="truncate">{sortFieldLabel(sort.field)}</span>
                  </span>
                ) : (
                  <Select
                    value={sort.field}
                    onValueChange={(value) =>
                      update(idx, { field: value })
                    }
                  >
                    <SelectTrigger className="h-8 flex-1 border-slate-200 bg-white text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SORTABLE_FIELDS.map((f) => {
                        const taken = usedFields.has(f.key) && f.key !== sort.field;
                        return (
                          <SelectItem
                            key={f.key}
                            value={f.key}
                            disabled={taken}
                          >
                            {f.label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                <Select
                  value={sort.dir}
                  onValueChange={(value) =>
                    update(idx, { dir: value as SortDirection })
                  }
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="h-8 w-[120px] border-slate-200 bg-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Ascending</SelectItem>
                    <SelectItem value="desc">Descending</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  aria-label={`Remove sort by ${sortFieldLabel(sort.field)}`}
                  className="rounded-md p-1.5 text-slate-400 transition-all hover:bg-rose-50 hover:text-rose-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
          {/* Draft row — visible after "+ Add sort" click, before
           *  the user picks a field. Mirrors a materialised row
           *  structurally but: (a) no priority number (the draft
           *  isn't in sorts yet), (b) reorder buttons disabled
           *  (nothing to reorder), (c) field Select uses a
           *  "Choose field…" placeholder with a dashed-border
           *  visual hint, (d) field-pick materialises into sorts
           *  via commitDraft, (e) × clears the draft locally
           *  without touching sorts. */}
          {draft !== null && (
            <li className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="flex items-center text-[10px] font-semibold uppercase tracking-wider text-slate-300"
              >
                +
              </span>
              <div className="flex flex-col">
                <button
                  type="button"
                  disabled
                  aria-label="Move sort up"
                  className="h-3 w-5 rounded text-slate-400 cursor-not-allowed opacity-30"
                >
                  <ArrowUp className="mx-auto h-3 w-3" />
                </button>
                <button
                  type="button"
                  disabled
                  aria-label="Move sort down"
                  className="h-3 w-5 rounded text-slate-400 cursor-not-allowed opacity-30"
                >
                  <ArrowDown className="mx-auto h-3 w-3" />
                </button>
              </div>
              <Select
                value={draft.field ?? ""}
                onValueChange={(value) => commitDraft(value)}
              >
                <SelectTrigger className="h-8 flex-1 border-dashed border-slate-300 bg-white text-xs">
                  <SelectValue placeholder="Choose field…" />
                </SelectTrigger>
                <SelectContent>
                  {SORTABLE_FIELDS.map((f) => (
                    <SelectItem
                      key={f.key}
                      value={f.key}
                      disabled={usedFields.has(f.key)}
                    >
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={draft.dir}
                onValueChange={(value) =>
                  setDraft({ ...draft, dir: value as SortDirection })
                }
              >
                <SelectTrigger className="h-8 w-[120px] border-slate-200 bg-white text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => setDraft(null)}
                aria-label="Discard draft sort"
                className="rounded-md p-1.5 text-slate-400 transition-all hover:bg-rose-50 hover:text-rose-500"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          )}
        </ul>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={add}
        // Disabled when (a) every field is already in use OR (b) a
        // draft is already pending — one-at-a-time per F4-bis D5.
        disabled={!firstUnused || draft !== null}
        className="h-7 text-xs text-indigo-600 hover:text-indigo-700"
      >
        <Plus className="mr-1 h-3 w-3" />
        Add sort
      </Button>
    </div>
  );
}
